// src/components/ModalEditarEgreso.js
import React, { useEffect, useState } from 'react';
import { FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import './ModalRegistroPago.css';
import { db } from '../firebase';
import { doc, getDoc, collection, query, orderBy, getDocs } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { normalizarCategoriasEgreso } from '../utils/bolsasFinancierasUtils';

/*
  Props:
  - egreso: objeto egreso actual
  - onClose: fn()
  - onConfirm: fn(updated) // puede contener comprobante nuevo opcional
*/
const ModalEditarEgreso = ({ egreso, onClose, onConfirm }) => {
  const { userData } = useAuth();
  const fraccionamientoId = userData?.fraccionamientoId;

  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmClose, setShowConfirmClose] = useState(false);

  const [monto, setMonto] = useState(egreso?.monto || 0);
  const [descuentoPorcentaje, setDescuentoPorcentaje] = useState(egreso?.descuentoPorcentaje || 0);
  const [metodoPago, setMetodoPago] = useState(egreso?.metodoPago || 'Transferencia Bancaria');
  const [tipoGasto, setTipoGasto] = useState(egreso?.tipoGasto || egreso?.['Tipo de Gasto:'] || 'Variable');
  const [fechaEgreso, setFechaEgreso] = useState(() => {
    try {
      const d = egreso?.fecha?.toDate ? egreso.fecha.toDate() : (egreso?.fecha ? new Date(egreso.fecha) : new Date());
      return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().split('T')[0];
    } catch { return new Date().toISOString().split('T')[0]; }
  });
  const [categoria, setCategoria] = useState(egreso?.categoria || '');
  const [descripcion, setDescripcion] = useState(egreso?.descripcion || '');
  const [proveedor, setProveedor] = useState(egreso?.proveedor || '');
  const [referenciaFactura, setReferenciaFactura] = useState(egreso?.referenciaFactura || '');
  const [referencia, setReferencia] = useState(egreso?.referencia || '');
  const [prioridad, setPrioridad] = useState(egreso?.prioridad || 'Media');
  const [nuevoComprobante, setNuevoComprobante] = useState(null); // opcional

  const [categoriasEgreso, setCategoriasEgreso] = useState([]);
  const [categoriasEstructuradas, setCategoriasEstructuradas] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [loadingProveedores, setLoadingProveedores] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchConceptosEgreso = async () => {
      if (!fraccionamientoId) return;
      try {
        const fraccRef = doc(db, 'fraccionamientos', fraccionamientoId);
        const snap = await getDoc(fraccRef);
        const cfg = snap.exists() ? snap.data().configuracionRecibos : null;
        const estructuradas = normalizarCategoriasEgreso(cfg?.categoriasEgreso, cfg?.conceptosEgresoList);
        setCategoriasEstructuradas(estructuradas);
        const list = Array.isArray(cfg?.conceptosEgresoList)
          ? cfg.conceptosEgresoList
          : cfg?.conceptosEgreso && typeof cfg.conceptosEgreso === 'object'
            ? Object.values(cfg.conceptosEgreso)
            : null;
        if (list && list.length) setCategoriasEgreso(list.filter(Boolean));
      } catch (err) {
        console.error('ModalEditarEgreso: conceptos egreso', err);
      }
    };
    const fetchProveedores = async () => {
      if (!fraccionamientoId) return;
      try {
        setLoadingProveedores(true);
        const ref = collection(db, 'fraccionamientos', fraccionamientoId, 'proveedores');
        const q = query(ref, orderBy('nombreEmpresa'));
        const snap = await getDocs(q);
        const list = snap.docs.map((d)=> ({ id: d.id, ...d.data() }));
        setProveedores(list);
      } catch (err) {
        console.error('ModalEditarEgreso: proveedores', err);
      } finally {
        setLoadingProveedores(false);
      }
    };
    fetchConceptosEgreso();
    fetchProveedores();
  }, [fraccionamientoId]);

  const metodosDePago = [
    'Transferencia Bancaria','Tarjeta de Crédito','Tarjeta de Débito','PayPal','Mercado Pago','Efectivo','Pago Cajero','Otros'
  ];
  const prioridades = ['Baja','Media','Alta','Urgente'];

  const sanitizeNumberInput = (value, allowDecimal = true) => {
    const regex = allowDecimal ? /[^0-9.]/g : /[^0-9]/g;
    return (value || '').replace(regex, '');
  };
  const preventNonNumeric = (evt) => {
    const { data, inputType } = evt.nativeEvent;
    if (inputType === 'deleteContentBackward' || inputType === 'deleteContentForward') return;
    if (!data) return;
    if (!/^[0-9.]$/.test(data)) evt.preventDefault();
  };

  const handleRequestClose = () => { setShowConfirmClose(true); };
  const confirmCloseModal = () => { setShowConfirmClose(false); onClose(); };
  const cancelCloseModal = () => { setShowConfirmClose(false); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    if (!monto || parseFloat(monto) <= 0) { setErrorMessage('⚠️ Monto: debe ser mayor a 0.'); return; }
    if (!fechaEgreso) { setErrorMessage('⚠️ Fecha del gasto: obligatoria.'); return; }
    if (!proveedor.trim()) { setErrorMessage('⚠️ Proveedor: obligatorio.'); return; }
    if (!referenciaFactura.trim()) { setErrorMessage('⚠️ Referencia/Factura: obligatoria.'); return; }
    if (!descripcion.trim() && !categoria.trim()) { setErrorMessage('⚠️ Concepto: ingresa descripción o categoría.'); return; }

    setIsSubmitting(true);
    const updated = {
      monto: parseFloat(monto || 0),
      descuentoPorcentaje: parseFloat(descuentoPorcentaje || 0),
      metodoPago,
      fecha: new Date(fechaEgreso),
      categoria: categoria || '',
      categoriaEgreso: categoria || '',
      tipoGasto: tipoGasto || 'Variable',
      descripcion: descripcion || '',
      proveedor,
      referenciaFactura,
      referencia: referencia || '',
      prioridad,
      comprobante: nuevoComprobante || null,
    };
    await onConfirm(updated);
    setIsSubmitting(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content egreso-modal" style={{ backgroundColor: '#fff7ed', border: '1px solid #ffedd5' }}>
        <button onClick={handleRequestClose} className="modal-close-btn"><FaTimes /></button>
        <h2>Editar Egreso</h2>
        {errorMessage && (
          <div className="form-error-message"><FaExclamationTriangle /> {errorMessage}</div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="monto">Monto</label>
              <input type="number" step="0.01" inputMode="decimal" pattern="[0-9]*[.,]?[0-9]*" id="monto" value={monto}
                onChange={(e)=>{ const clean = sanitizeNumberInput(e.target.value,true); setMonto(clean === '' ? '' : parseFloat(clean)); }}
                onBeforeInput={preventNonNumeric} />
            </div>
            <div className="form-group">
              <label htmlFor="fechaEgreso">Fecha</label>
              <input type="date" id="fechaEgreso" value={fechaEgreso} onChange={(e)=> setFechaEgreso(e.target.value)} />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="categoria">
                Categoría / Concepto
                <span className={`badge-tipo-gasto ${tipoGasto === 'Fijo' ? 'fijo' : 'variable'}`}>
                  {tipoGasto === 'Fijo' ? 'Fijo' : 'Variable'}
                </span>
              </label>
              <select id="categoria" value={categoria} onChange={(e)=> {
                const val = e.target.value;
                setCategoria(val);
                for (const cat of categoriasEstructuradas) {
                  const conc = (cat.conceptos || []).find(c => (typeof c === 'string' ? c : c.nombre) === val);
                  if (conc && typeof conc === 'object' && conc.tipoGasto) {
                    setTipoGasto(conc.tipoGasto);
                    break;
                  }
                }
              }}>
                <option value="">Seleccionar…</option>
                {categoriasEgreso.map(cat=> (<option key={cat} value={cat}>{cat}</option>))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="metodoPago">Método de Pago</label>
              <select id="metodoPago" value={metodoPago} onChange={(e)=> setMetodoPago(e.target.value)}>
                {metodosDePago.map(m=> (<option key={m} value={m}>{m}</option>))}
              </select>
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="descuentoPorcentaje">Descuento (%)</label>
              <input type="number" id="descuentoPorcentaje" min="0" step="0.01" inputMode="decimal" pattern="[0-9]*[.,]?[0-9]*" value={descuentoPorcentaje}
                onChange={(e)=>{ const clean = sanitizeNumberInput(e.target.value,true); setDescuentoPorcentaje(clean === '' ? '' : clean); }}
                onBeforeInput={preventNonNumeric} />
            </div>
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="prioridad">Prioridad</label>
              <select id="prioridad" value={prioridad} onChange={(e)=> setPrioridad(e.target.value)}>
                {prioridades.map(p=> (<option key={p} value={p}>{p}</option>))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="referencia">Referencia interna (opcional)</label>
              <input type="text" id="referencia" value={referencia} onChange={(e)=> setReferencia(e.target.value)} />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="proveedor">Proveedor</label>
            <input type="text" id="proveedor" value={proveedor} onChange={(e)=> setProveedor(e.target.value)} list="proveedores-list" placeholder={loadingProveedores ? 'Cargando…' : 'Nombre o razón social'} />
            <datalist id="proveedores-list">
              {proveedores.map(p=> (<option key={p.id} value={p.nombreEmpresa || ''}>{(p.nombreEmpresa || '').trim()}</option>))}
            </datalist>
          </div>

          <div className="form-group">
            <label htmlFor="referenciaFactura">Referencia / No. de Factura</label>
            <input type="text" id="referenciaFactura" value={referenciaFactura} onChange={(e)=> setReferenciaFactura(e.target.value)} />
          </div>

          <div className="form-group form-group-file">
            <label>Comprobante (opcional)</label>
            <input 
              type="file" 
              id="editar-comprobante-file"
              accept="image/*,.pdf" 
              onChange={(e)=> setNuevoComprobante(e.target.files[0] || null)} 
              style={{ display: 'none' }}
            />
            <div className="custom-file-upload">
              <label htmlFor="editar-comprobante-file" className="file-upload-button">
                <span>{nuevoComprobante ? 'Cambiar archivo' : 'Adjuntar Comprobante'}</span>
              </label>
              {nuevoComprobante && (
                <div className="file-name-badge">
                  <span className="file-icon-mini">{nuevoComprobante.name?.toLowerCase().endsWith('.pdf') ? 'PDF' : 'IMG'}</span>
                  <span className="file-name-text">{nuevoComprobante.name}</span>
                  <button type="button" className="btn-remove-file" onClick={()=> setNuevoComprobante(null)}>×</button>
                </div>
              )}
            </div>
            <small className="cp-muted">Máximo 5MB • PDF, JPG o PNG</small>
          </div>

          <div className="form-group">
            <label htmlFor="descripcion">Descripción / Concepto</label>
            <textarea id="descripcion" value={descripcion} onChange={(e)=> setDescripcion(e.target.value)} rows="3" placeholder="Detalle del egreso" />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={handleRequestClose} className="btn-cancel" disabled={isSubmitting}>Cancelar</button>
            <button type="submit" className="btn-confirm" disabled={isSubmitting} style={{ backgroundColor: '#d9534f' }}>{isSubmitting ? 'Guardando…' : 'Guardar cambios'}</button>
          </div>
        </form>
      </div>

      {showConfirmClose && (
        <div className="confirm-leave-backdrop">
          <div className="confirm-leave-modal">
            <h4>¿Cerrar el formulario?</h4>
            <p>Los cambios no guardados se perderán.</p>
            <div className="confirm-leave-actions">
              <button type="button" className="btn-secondary" onClick={cancelCloseModal}>Seguir editando</button>
              <button type="button" className="btn-danger" onClick={confirmCloseModal}>Salir y descartar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModalEditarEgreso;
